// Conversions API — typed fetch wrappers and React Query hooks. Mirrors the
// pattern in ptc-statetax/src/services/complianceApi.ts: fetchApi() helper,
// MSAL silent-token acquisition, react-query hooks for read + mutate.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { apiErrorMessage, journalValidationMessage, readErrorBody, timeoutMessage, type JournalValidationErrorBody } from '../lib/apiErrors';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { getMsalInstance } from '../auth/msalInstance';
import { apiScopes } from '../config/authConfig';
import { AuthRequiredError } from '../lib/authError';
import { FEEDBACK_SCOPE as CORE_SCOPE } from '../feedback/feedbackApi';
import type {
  ApiEnvelope,
  GradeRuling,
  GradingRun,
  RulingKind,
  ClientGlMap,
  ClientSummary,
  ConversionHistoryFilters,
  ConversionRecord,
  DocumentCategory,
  Dr15Extract,
  Dr15ExtractionResult,
  ExtractionAccuracyReport,
  ExtractionRun,
  JournalProposal,
  JournalProposalRequest,
  JournalTemplateInfo,
  ManualJournal,
  SalesSplit,
  PosReportReadResult,
  UploadedDocument,
  XeroTenantLink,
  XeroTenantProvenance,
  XeroTenantResult,
} from '../types';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';
const TIMEOUT_MS = 30_000;

/**
 * The access token, or a thrown {@link AuthRequiredError} naming why there isn't one.
 *
 * 🔴 **This used to return `null` on four separate paths, and every caller then guarded the
 * `Authorization` header on the token being truthy — which omitted the header and sent the
 * request anyway.** See `lib/authError.ts` for the incident that produced. Throwing is what
 * makes "we have no token" impossible to accidentally ignore: there is no falsy value left
 * to drop on the floor.
 *
 * ⚠ The forbidden expression is deliberately NOT quoted verbatim anywhere in this file — a
 * source-text guard in `authError.test.ts` asserts it is absent, and prose *about* the bug
 * would trip it. Reword the comment; never weaken the guard.
 *
 * The `interaction-required` path is the one worth pausing on. MSAL has already begun a
 * full-page redirect by then, so the *only* correct action is to abandon the request —
 * firing it unauthenticated raced a doomed 401 against the navigation, which is how a
 * spurious error could flash on screen on the way out.
 */
async function requireAccessToken(): Promise<string> {
  // The instance is injected during boot rather than constructed at module scope (see
  // auth/msalInstance.ts), so it is undefined until initialization completes. Nothing should
  // reach here before then — React only renders after init — but a bare "cannot read
  // properties of undefined" is exactly the illegible failure the boot guard exists to
  // abolish, so name it instead.
  const msalInstance = getMsalInstance();
  if (!msalInstance) {
    console.error('requireAccessToken called before MSAL finished initializing');
    throw new AuthRequiredError('not-initialized');
  }

  const account = msalInstance.getActiveAccount();
  if (!account) throw new AuthRequiredError('no-account');

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: apiScopes,
      account,
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes: apiScopes });
      throw new AuthRequiredError('interaction-required');
    }
    console.error('Failed to acquire token:', error);
    throw new AuthRequiredError('acquisition-failed');
  }
}

/**
 * Whether an endpoint needs a token.
 *
 * ⚠ `'anonymous'` exists for exactly one route and must stay that way. `Health` is the sole
 * entry in `AuthMiddleware.AnonymousFunctions` — everything else on the app is default-deny,
 * so requiring a token everywhere else is not a policy choice here, it is the server's.
 * Adding a second `'anonymous'` caller without a matching server-side entry produces a
 * request that is *sent* unauthenticated and then rejected: precisely the behaviour this
 * change removes.
 */
type AuthMode = 'required' | 'anonymous';

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit,
  auth: AuthMode = 'required',
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (auth === 'required') {
      headers['Authorization'] = `Bearer ${await requireAccessToken()}`;
    }

    if (options?.headers) {
      Object.assign(headers, options.headers as Record<string, string>);
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(apiErrorMessage(response.status, await readErrorBody(response)));
    }

    return response.json() as Promise<T>;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(timeoutMessage());
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ── Health probe ──────────────────────────────────────────────────────────────

export async function fetchHealth(): Promise<{ status: string; service: string; timestampUtc: string }> {
  // The one anonymous route on the app (`AuthMiddleware.AnonymousFunctions`). Marked
  // explicitly so that a liveness probe still works while signed OUT — which is when
  // someone is most likely to want to ask whether the API is up at all.
  return fetchApi('/health', undefined, 'anonymous');
}

// ── Client directory ─────────────────────────────────────────────────────────
//
// Backs the search-by-company-name lookup that replaced every free-text GUID
// field (CONVERSIONS_SPEC.md §3). The search envelope carries an optional
// `notice` so the UI can explain a pasted GUID instead of showing a bare
// empty result the user has to interpret.

export const clientKeys = {
  all: ['clients'] as const,
  search: (term: string) => [...clientKeys.all, 'search', term] as const,
  byId: (clientId: string) => [...clientKeys.all, clientId] as const,
};

/** Below this, the picker doesn't search — matches IClientDirectory.MinimumSearchTermLength. */
export const MIN_CLIENT_SEARCH_LENGTH = 2;

export interface ClientSearchResult {
  clients: ClientSummary[];
  /** `looksLikeGuid` when the term was a pasted id rather than a name. */
  notice?: string;
}

export async function searchClients(term: string): Promise<ClientSearchResult> {
  const envelope = await fetchApi<ApiEnvelope<ClientSummary[]> & { notice?: string }>(
    `/clients?search=${encodeURIComponent(term)}`,
  );
  return { clients: envelope.data ?? [], notice: envelope.notice };
}

export function useClientSearch(term: string) {
  const trimmed = term.trim();
  return useQuery({
    queryKey: clientKeys.search(trimmed),
    queryFn: () => searchClients(trimmed),
    enabled: trimmed.length >= MIN_CLIENT_SEARCH_LENGTH,
    // Results are stable between keystrokes; don't re-hit the directory on refocus.
    staleTime: 5 * 60_000,
  });
}

export async function fetchClient(clientId: string): Promise<ClientSummary | null> {
  const envelope = await fetchApi<ApiEnvelope<ClientSummary>>(
    `/clients/${encodeURIComponent(clientId)}`,
  );
  return envelope.data ?? null;
}

/**
 * Resolves a stored clientId to a display name. Used wherever a GUID was
 * previously shown — a 404 resolves to null, which the caller renders as a
 * visible orphan marker rather than dropping the row.
 */
export function useClient(clientId: string) {
  return useQuery({
    queryKey: clientKeys.byId(clientId),
    queryFn: () => fetchClient(clientId).catch(() => null),
    enabled: !!clientId,
    staleTime: 5 * 60_000,
  });
}

// ── Xero organisation link (C8) ───────────────────────────────────────────────

export const xeroTenantKeys = {
  all: ['xero-tenant'] as const,
  byClient: (clientId: string) => [...xeroTenantKeys.all, clientId] as const,
};

/**
 * The client's linked Xero organisation, or null when there is none.
 *
 * ⚠ null is the normal state — the endpoint answers 200 with `data: null` rather
 * than 404, so "not linked" never arrives as an error the caller has to catch.
 * A thrown error here means the link could not be READ, which is a different
 * thing and must not be rendered as "not linked".
 */
export async function fetchClientXeroTenant(clientId: string): Promise<XeroTenantResult> {
  const envelope = await fetchApi<ApiEnvelope<XeroTenantLink | null> & { provenance?: XeroTenantProvenance | null }>(
    `/clients/${encodeURIComponent(clientId)}/xero-tenant`,
  );
  return { link: envelope.data ?? null, provenance: envelope.provenance ?? null };
}

export function useClientXeroTenant(clientId: string) {
  return useQuery({
    queryKey: xeroTenantKeys.byClient(clientId),
    queryFn: () => fetchClientXeroTenant(clientId),
    enabled: !!clientId,
  });
}

// ── GL maps ───────────────────────────────────────────────────────────────────

export const glMapKeys = {
  all: ['gl-maps'] as const,
  byClient: (clientId: string) => [...glMapKeys.all, clientId] as const,
};

export async function fetchClientGlMap(clientId: string): Promise<ClientGlMap | null> {
  const envelope = await fetchApi<ApiEnvelope<ClientGlMap | null>>(
    `/gl-maps/${encodeURIComponent(clientId)}`,
  );
  return envelope.data ?? null;
}

export function useClientGlMap(clientId: string) {
  return useQuery({
    queryKey: glMapKeys.byClient(clientId),
    queryFn: () => fetchClientGlMap(clientId),
    enabled: !!clientId,
  });
}

export async function upsertClientGlMap(
  clientId: string,
  payload: Partial<ClientGlMap>,
): Promise<ClientGlMap> {
  const envelope = await fetchApi<ApiEnvelope<ClientGlMap>>(
    `/gl-maps/${encodeURIComponent(clientId)}`,
    { method: 'PUT', body: JSON.stringify({ ...payload, clientId }) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? i18next.t('glMappingPage.errors.saveFailed'));
  return envelope.data;
}

export function useUpsertClientGlMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      payload,
    }: {
      clientId: string;
      payload: Partial<ClientGlMap>;
    }) => upsertClientGlMap(clientId, payload),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: glMapKeys.byClient(variables.clientId) });
    },
  });
}

/** How many journals name each account code, removed ones included (§B6). Absent = none: only then may it be deleted. */
export function useGlAccountUsage(clientId: string) {
  return useQuery({
    queryKey: [...glMapKeys.byClient(clientId), 'account-usage'],
    queryFn: async () => {
      const envelope = await fetchApi<ApiEnvelope<Record<string, number>>>(
        `/gl-maps/${encodeURIComponent(clientId)}/account-usage`,
      );
      return envelope.data ?? {};
    },
    enabled: !!clientId,
  });
}

/** Change an account's code; every journal line on it follows (§B3). */
export function useChangeGlAccountCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, from, to }: { clientId: string; from: string; to: string }) => {
      const envelope = await fetchApi<ApiEnvelope<{ journalsChanged: number; map: ClientGlMap }>>(
        `/gl-maps/${encodeURIComponent(clientId)}/account-code`,
        { method: 'POST', body: JSON.stringify({ from, to }) },
      );
      if (!envelope.data) throw new Error(envelope.error ?? i18next.t('glMappingPage.errors.saveFailed'));
      return envelope.data;
    },
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: glMapKeys.byClient(variables.clientId) });
      void qc.invalidateQueries({ queryKey: journalKeys.byClient(variables.clientId) });
    },
  });
}

// ── The signed-in person's preferences (fleet i18n standard §3) ──────────────

/** `null` = never chosen (or cleared): the browser's own choice stands. There is no default. */
export type UiLanguage = 'en' | 'es';

export interface UserPreferences {
  language: UiLanguage | null;
  /** Accomplishment moments' off switch + seen-once ids (docs/accomplishment-moments.md). Absent = on, nothing seen. */
  moments?: MomentPreferences | null;
  /** The client this person last had selected (docs/client-context.md): a Dataverse account id, or null. */
  lastClientId?: string | null;
}

export interface MomentPreferences {
  enabled: boolean;
  seen: string[];
}

export const userPreferenceKeys = {
  mine: ['me', 'preferences'] as const,
};

export async function fetchUserPreferences(): Promise<UserPreferences> {
  const envelope = await fetchApi<ApiEnvelope<UserPreferences>>('/me/preferences');
  return envelope.data ?? { language: null };
}

/**
 * The person's saved preferences. Never retried: a failed read must not hold up anything —
 * the toggle simply keeps the browser's choice — and a retry loop on every screen would be noise.
 */
export function useUserPreferences() {
  return useQuery({
    queryKey: userPreferenceKeys.mine,
    queryFn: fetchUserPreferences,
    retry: false,
    staleTime: Infinity,
  });
}

/** The header this app's API reads core's token from (`BearerTokenHelpers.CoreTokenHeader`). */
export const CORE_TOKEN_HEADER = 'X-Ptc-Core-Token';

/**
 * The person's token for ptc-CORE's API (the CRM scope the feedback widget already uses), for the calls where this
 * app's API asks core who they are: whether they are an Admin. Core refuses this app's own token (a token is minted for
 * one audience), which is why the admin check read "not admin" for everyone until deploy #22. Taken through MSAL's own
 * `acquireTokenSilent`, never from storage. No token (no grant, no account) sends no header, and the server then
 * answers "not admin": fails closed.
 */
export async function coreTokenHeader(): Promise<Record<string, string>> {
  try {
    const msal = getMsalInstance();
    const account = msal?.getActiveAccount() ?? msal?.getAllAccounts()[0];
    if (!msal || !account) return {};
    const { accessToken } = await msal.acquireTokenSilent({ scopes: [CORE_SCOPE], account });
    return accessToken ? { [CORE_TOKEN_HEADER]: accessToken } : {};
  } catch {
    return {};
  }
}

/**
 * What the signed-in person may SEE, as ptc-core decides it (`GET /api/me/access`): today only whether they are one of
 * core's Admins, who see the nav's DEV section. Fails closed: an error or no answer reads as NOT admin. Never retried,
 * and kept for the session: it changes when core's roles change, not while a page is open.
 */
export async function fetchMyAccess(): Promise<{ isAdmin: boolean }> {
  const envelope = await fetchApi<ApiEnvelope<{ isAdmin: boolean }>>('/me/access', { headers: await coreTokenHeader() });
  return { isAdmin: envelope.data?.isAdmin === true };
}

export function useMyAccess() {
  return useQuery({
    queryKey: ['me', 'access'] as const,
    queryFn: fetchMyAccess,
    retry: false,
    staleTime: Infinity,
  });
}

export async function saveLanguagePreference(language: UiLanguage): Promise<UserPreferences> {
  const envelope = await fetchApi<ApiEnvelope<UserPreferences>>('/me/preferences', {
    method: 'PUT',
    body: JSON.stringify({ language }),
  });
  return envelope.data ?? { language };
}

export function useSaveLanguagePreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveLanguagePreference,
    onSuccess: (data) => {
      qc.setQueryData(userPreferenceKeys.mine, data);
    },
  });
}

/** Saves ONLY the moments field; the server merges, so the saved language is untouched. */
export async function saveMomentPreferences(moments: MomentPreferences): Promise<UserPreferences> {
  const envelope = await fetchApi<ApiEnvelope<UserPreferences>>('/me/preferences', {
    method: 'PUT',
    body: JSON.stringify({ moments }),
  });
  return envelope.data ?? { language: null, moments };
}

export function useSaveMomentPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveMomentPreferences,
    onSuccess: (data) => {
      qc.setQueryData(userPreferenceKeys.mine, data);
    },
  });
}

/** Saves ONLY the remembered client (docs/client-context.md); the server merges, so language and moments stay. */
export async function saveLastClientPreference(lastClientId: string | null): Promise<UserPreferences> {
  const envelope = await fetchApi<ApiEnvelope<UserPreferences>>('/me/preferences', {
    method: 'PUT',
    body: JSON.stringify({ lastClientId }),
  });
  return envelope.data ?? { language: null, lastClientId };
}

/**
 * OPTIMISTIC: the cached preference changes before the request, so a screen opened right after a clear never re-applies
 * the client that was just cleared. TanStack runs `onMutate` synchronously inside `mutate` (measured 2026-10-27,
 * lastClientPreference.test.tsx, with no act()), so the cache has changed by the time the click handler returns. A failed
 * save puts the previous value back.
 */
export function useSaveLastClientPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveLastClientPreference,
    onMutate: (lastClientId) => {
      const previous = qc.getQueryData<UserPreferences>(userPreferenceKeys.mine);
      qc.setQueryData<UserPreferences>(userPreferenceKeys.mine, { language: null, ...previous, lastClientId });
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) qc.setQueryData(userPreferenceKeys.mine, context.previous);
    },
    onSuccess: (data) => {
      qc.setQueryData(userPreferenceKeys.mine, data);
    },
  });
}

// ── Manual-journal CSV export ────────────────────────────────────────────────
//
// Unlike fetchApi (which assumes a JSON envelope), the export endpoint returns
// text/csv with Content-Disposition: attachment. We need the blob + the
// filename + the summary header for download UX. So this path uses raw fetch
// and surfaces HTTP-level errors as a typed ExportError so the UI can branch
// on 412 (no GL map configured) vs 400 (validation) vs everything else.

export class ExportError extends Error {
  constructor(
    public readonly status: number,
    public readonly serverError: string,
    /**
     * Populated on a 412 from the template/propose and journal endpoints: the GL
     * roles this journal needed and could not find. Lets the UI name them and link
     * to the mapping page instead of showing a wall of prose.
     */
    public readonly missingRoles?: string[],
  ) {
    super(serverError);
    this.name = 'ExportError';
  }
}

export interface ManualJournalResult {
  blob: Blob;
  filename: string;
  summary: string;
}

export async function exportManualJournal(
  clientId: string,
  period: string,
  extract: Partial<Dr15Extract>,
): Promise<ManualJournalResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const token = await requireAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    headers['Authorization'] = `Bearer ${token}`;

    const url = `${API_BASE}/export/manual-journal/${encodeURIComponent(clientId)}/${encodeURIComponent(period)}`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(extract),
      signal: controller.signal,
    });

    if (!response.ok) {
      // The error path returns JSON: { success: false, error: "..." }
      throw new ExportError(response.status, apiErrorMessage(response.status, await readErrorBody(response)));
    }

    const blob = await response.blob();
    const filename = parseFilename(response.headers.get('Content-Disposition')) ??
      `manual-journal-${clientId}-${period}.csv`;
    const summary = response.headers.get('X-Conversion-Summary') ?? '';
    return { blob, filename, summary };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ExportError(0, timeoutMessage());
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

function parseFilename(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;
  // RFC 6266 attachment; filename="..." — handles both quoted and unquoted.
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
  return match ? decodeURIComponent(match[1]) : null;
}

export function useExportManualJournal() {
  return useMutation({
    mutationFn: ({
      clientId,
      period,
      extract,
    }: {
      clientId: string;
      period: string;
      extract: Partial<Dr15Extract>;
    }) => exportManualJournal(clientId, period, extract),
  });
}

// ── Bank-statement (3b) / account-transactions (3c) CSV export ──────────────
//
// Both take the same shape: staff uploads a bank-export file, we POST the raw
// bytes (not multipart, not JSON — mirrors the backend's ParseAndBuildAsync
// which reads req.Body directly), and get a CSV blob back. v1 data-source
// assumption: uploaded bank-export files (see PR notes) — no POS/Dataverse
// feed yet.

export interface FileExportResult {
  blob: Blob;
  filename: string;
  summary: string;
}

async function exportFileUpload(endpointPath: string, file: File): Promise<FileExportResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const token = await requireAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': file.type || 'text/csv',
      'X-Source-Filename': file.name,
    };
    headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE}${endpointPath}`, {
      method: 'POST',
      headers,
      body: file,
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new ExportError(response.status, apiErrorMessage(response.status, await readErrorBody(response)));
    }

    const blob = await response.blob();
    const filename = parseFilename(response.headers.get('Content-Disposition')) ?? 'export.csv';
    const summary = response.headers.get('X-Conversion-Summary') ?? '';
    return { blob, filename, summary };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ExportError(0, timeoutMessage());
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function exportBankStatement(clientId: string, file: File): Promise<FileExportResult> {
  return exportFileUpload(`/export/bank-statement/${encodeURIComponent(clientId)}`, file);
}

export function exportAccountTransactions(clientId: string, file: File): Promise<FileExportResult> {
  return exportFileUpload(`/export/account-transactions/${encodeURIComponent(clientId)}`, file);
}

export function useExportBankStatement() {
  return useMutation({
    mutationFn: ({ clientId, file }: { clientId: string; file: File }) =>
      exportBankStatement(clientId, file),
  });
}

export function useExportAccountTransactions() {
  return useMutation({
    mutationFn: ({ clientId, file }: { clientId: string; file: File }) =>
      exportAccountTransactions(clientId, file),
  });
}

// ── Categorized upload intake ────────────────────────────────────────────────
//
// Uploads are first-class records now (docs/categorized-upload-intake.md): the
// staff-picked category rides the query string, the raw bytes are the POST body
// (same convention as the raw-body export endpoints), and the backend stores
// blob + metadata. The from-upload export endpoints then run 3b/3c off the
// STORED document — no re-picking the file per export.

export const uploadKeys = {
  all: ['uploads'] as const,
  byClient: (clientId: string) => [...uploadKeys.all, clientId] as const,
};

export async function uploadDocument(
  clientId: string,
  category: DocumentCategory,
  file: File,
): Promise<UploadedDocument> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const token = await requireAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': file.type || 'application/octet-stream',
      'X-Source-Filename': file.name,
    };
    headers['Authorization'] = `Bearer ${token}`;

    const url = `${API_BASE}/uploads/${encodeURIComponent(clientId)}?category=${encodeURIComponent(category)}`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: file,
      signal: controller.signal,
    });

    const envelope = (await response.json().catch(() => null)) as ApiEnvelope<UploadedDocument> | null;
    if (!response.ok || !envelope?.data) {
      throw new ExportError(response.status, apiErrorMessage(response.status, envelope));
    }
    return envelope.data;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ExportError(0, timeoutMessage());
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function useUploadDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      category,
      file,
    }: {
      clientId: string;
      category: DocumentCategory;
      file: File;
    }) => uploadDocument(clientId, category, file),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: uploadKeys.byClient(variables.clientId) });
    },
  });
}

export async function fetchUploads(clientId: string, includeRemoved = false): Promise<UploadedDocument[]> {
  const envelope = await fetchApi<ApiEnvelope<UploadedDocument[]>>(
    `/uploads/${encodeURIComponent(clientId)}${includeRemoved ? '?includeRemoved=true' : ''}`,
  );
  return envelope.data ?? [];
}

/** `includeRemoved`: removed uploads are kept (DP-CNV-BACKUP) and listed, for restoring, only when asked for. */
export function useUploads(clientId: string, includeRemoved = false) {
  return useQuery({
    queryKey: [...uploadKeys.byClient(clientId), { includeRemoved }],
    queryFn: () => fetchUploads(clientId, includeRemoved),
    enabled: !!clientId,
  });
}

/** A short-lived, read-only link to view one upload's file (View document, 2026-10-29). Never cached, never retried. */
export interface UploadViewLink {
  url: string;
  filename: string;
  expiresAt: string;
}

export function useUploadViewLink(clientId: string, documentId: string, enabled = true) {
  return useQuery({
    queryKey: [...uploadKeys.byClient(clientId), 'view', documentId],
    queryFn: async () => {
      const envelope = await fetchApi<ApiEnvelope<UploadViewLink>>(
        `/uploads/${encodeURIComponent(clientId)}/${encodeURIComponent(documentId)}/view-url`,
      );
      if (!envelope.data) throw new Error(envelope.error ?? i18next.t('documentView.failed'));
      return envelope.data;
    },
    enabled: enabled && !!clientId && !!documentId,
    // The link is a grant that expires: always a fresh one, and a failure is said, not retried behind a spinner.
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
}

/** An upload's bytes, for View to draw in its own page (pdf.js). Same-origin, behind the gate; never cached. */
export interface UploadContent {
  bytes: ArrayBuffer;
  contentType: string;
}

export async function fetchUploadContent(clientId: string, documentId: string): Promise<UploadContent> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const token = await requireAccessToken();
    const response = await fetch(
      `${API_BASE}/uploads/${encodeURIComponent(clientId)}/${encodeURIComponent(documentId)}/content`,
      { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal, cache: 'no-store' },
    );
    if (!response.ok) throw new ExportError(response.status, apiErrorMessage(response.status, await readErrorBody(response)));
    return { bytes: await response.arrayBuffer(), contentType: response.headers.get('Content-Type') ?? '' };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw new ExportError(0, timeoutMessage());
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function useUploadContent(clientId: string, documentId: string, enabled = true) {
  return useQuery({
    queryKey: [...uploadKeys.byClient(clientId), 'content', documentId],
    queryFn: () => fetchUploadContent(clientId, documentId),
    enabled: enabled && !!clientId && !!documentId,
    gcTime: 0,
    staleTime: Infinity,
    retry: false,
  });
}

export async function restoreUpload(clientId: string, documentId: string): Promise<void> {
  await fetchApi<ApiEnvelope<never>>(
    `/uploads/${encodeURIComponent(clientId)}/${encodeURIComponent(documentId)}/restore`,
    { method: 'POST' },
  );
}

export function useRestoreUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, documentId }: { clientId: string; documentId: string }) =>
      restoreUpload(clientId, documentId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: uploadKeys.byClient(variables.clientId) });
    },
  });
}

export async function deleteUpload(clientId: string, documentId: string): Promise<void> {
  await fetchApi<ApiEnvelope<never>>(
    `/uploads/${encodeURIComponent(clientId)}/${encodeURIComponent(documentId)}`,
    { method: 'DELETE' },
  );
}

export function useDeleteUpload() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, documentId }: { clientId: string; documentId: string }) =>
      deleteUpload(clientId, documentId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: uploadKeys.byClient(variables.clientId) });
    },
  });
}

// From-upload CSV exports — same download shape as the raw-body path, but the
// source is the stored categorized upload (bankStatement documents only; the
// backend 400s anything else with routing guidance).

async function exportFromUpload(endpointPath: string): Promise<FileExportResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const token = await requireAccessToken();
    const headers: Record<string, string> = {};
    headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE}${endpointPath}`, {
      method: 'POST',
      headers,
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new ExportError(response.status, apiErrorMessage(response.status, await readErrorBody(response)));
    }

    const blob = await response.blob();
    const filename = parseFilename(response.headers.get('Content-Disposition')) ?? 'export.csv';
    const summary = response.headers.get('X-Conversion-Summary') ?? '';
    return { blob, filename, summary };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ExportError(0, timeoutMessage());
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function exportBankStatementFromUpload(
  clientId: string,
  documentId: string,
): Promise<FileExportResult> {
  return exportFromUpload(
    `/export/bank-statement/${encodeURIComponent(clientId)}/from-upload/${encodeURIComponent(documentId)}`,
  );
}

export function exportAccountTransactionsFromUpload(
  clientId: string,
  documentId: string,
): Promise<FileExportResult> {
  return exportFromUpload(
    `/export/account-transactions/${encodeURIComponent(clientId)}/from-upload/${encodeURIComponent(documentId)}`,
  );
}

export function useExportBankStatementFromUpload() {
  return useMutation({
    mutationFn: ({ clientId, documentId }: { clientId: string; documentId: string }) =>
      exportBankStatementFromUpload(clientId, documentId),
  });
}

export function useExportAccountTransactionsFromUpload() {
  return useMutation({
    mutationFn: ({ clientId, documentId }: { clientId: string; documentId: string }) =>
      exportAccountTransactionsFromUpload(clientId, documentId),
  });
}

// ── DR-15 extraction (Slice 2) ───────────────────────────────────────────────
//
// Runs the backend extractor over a stored dr15Return upload (cheap PdfPig+regex
// path first, Document Intelligence + GPT-4o for scans / low-confidence parses).
// Non-DR-15 documents are a 400 with routing guidance — surface the server
// message verbatim. The result carries crossCheckPassed so the UI can warn when
// the printed figures didn't reconcile.

/**
 * B1 (docs/core-workflow.md): one extraction run's kept figures, for the journal page to propose from. A 404 (the run
 * failed, predates the shadow, or is another client's) is an ERROR here, never an empty set of figures.
 */
export async function fetchExtractionRun(clientId: string, runId: string): Promise<ExtractionRun> {
  const envelope = await fetchApi<ApiEnvelope<ExtractionRun>>(
    `/extract/dr15/${encodeURIComponent(clientId)}/runs/${encodeURIComponent(runId)}`,
  );
  if (!envelope.data) throw new Error(apiErrorMessage(404, envelope));
  return envelope.data;
}

/**
 * Mission item 4: the grade, for one client or (no client) every client. Read-only; NO DATA until a DR-15 is extracted
 * and its journal exported. Accuracy per client: the report carries each client's streak.
 */
/** Accuracy §2, admin-only: grade one month (`yyyy-MM`) and store it. */
export function useRunGradingMonth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (month: string) => {
      const envelope = await fetchApi<ApiEnvelope<GradingRun>>('/extraction-accuracy/runs', {
        method: 'POST',
        headers: await coreTokenHeader(),
        body: JSON.stringify({ month }),
      });
      if (!envelope.data) throw new Error(apiErrorMessage(500, envelope));
      return envelope.data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['extraction-accuracy'] }),
  });
}

/** Accuracy §2, admin-only: a ruling for a miss class. */
export function useRuleGradeClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ruling: { missClass: string; kind: RulingKind; fixDeployedAt?: string | null; note?: string | null }) => {
      const envelope = await fetchApi<ApiEnvelope<GradeRuling>>('/extraction-accuracy/rulings', {
        method: 'POST',
        headers: await coreTokenHeader(),
        body: JSON.stringify(ruling),
      });
      if (!envelope.data) throw new Error(apiErrorMessage(500, envelope));
      return envelope.data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['extraction-accuracy'] }),
  });
}

export function useExtractionAccuracy(clientId = '', enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['extraction-accuracy', clientId],
    queryFn: async () => {
      const query = clientId ? `?${new URLSearchParams({ clientId }).toString()}` : '';
      // Core's token rides along so the server can tell an Admin, who alone sees the Dependable badge.
      const envelope = await fetchApi<ApiEnvelope<ExtractionAccuracyReport>>(`/extraction-accuracy${query}`, {
        headers: await coreTokenHeader(),
      });
      if (!envelope.data) throw new Error(apiErrorMessage(500, envelope));
      return envelope.data;
    },
  });
}

export function useExtractionRun(clientId: string, runId: string | null) {
  return useQuery({
    queryKey: ['extraction-run', clientId, runId],
    queryFn: () => fetchExtractionRun(clientId, runId!),
    enabled: !!clientId && !!runId,
    retry: false,
  });
}

export async function extractDr15FromUpload(
  clientId: string,
  documentId: string,
): Promise<Dr15ExtractionResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), EXTRACT_TIMEOUT_MS);

  try {
    const token = await requireAccessToken();
    const headers: Record<string, string> = {};
    headers['Authorization'] = `Bearer ${token}`;

    const url = `${API_BASE}/extract/dr15/${encodeURIComponent(clientId)}/from-upload/${encodeURIComponent(documentId)}`;
    const response = await fetch(url, { method: 'POST', headers, signal: controller.signal });

    const envelope = (await response
      .json()
      .catch(() => null)) as ApiEnvelope<Dr15ExtractionResult> | null;
    if (!response.ok || !envelope?.data) {
      throw new ExportError(response.status, apiErrorMessage(response.status, envelope));
    }
    return envelope.data;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ExportError(0, timeoutMessage());
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// Document Intelligence + GPT-4o on a scan can exceed the default 30s budget.
const EXTRACT_TIMEOUT_MS = 120_000;

export function useExtractDr15FromUpload() {
  return useMutation({
    mutationFn: ({ clientId, documentId }: { clientId: string; documentId: string }) =>
      extractDr15FromUpload(clientId, documentId),
  });
}

// ── Manual journals + templates (Slices C3 / C5) ────────────────────────────
//
// The journal is the primary object; the DR-15 is one input among several. These
// replace the deprecated period-driven export above, which stays live only until
// the UI stops calling it.

export const journalKeys = {
  all: ['journals'] as const,
  byClient: (clientId: string) => [...journalKeys.all, clientId] as const,
  templates: ['journal-templates'] as const,
};

export async function fetchJournalTemplates(): Promise<JournalTemplateInfo[]> {
  const envelope = await fetchApi<ApiEnvelope<JournalTemplateInfo[]>>('/journal-templates');
  return envelope.data ?? [];
}

export function useJournalTemplates() {
  return useQuery({
    queryKey: journalKeys.templates,
    queryFn: fetchJournalTemplates,
    // The template list is code, not data — it cannot change without a deploy.
    staleTime: Infinity,
  });
}

export async function fetchJournals(clientId: string): Promise<ManualJournal[]> {
  const envelope = await fetchApi<ApiEnvelope<ManualJournal[]>>(
    `/journals/${encodeURIComponent(clientId)}`,
  );
  return envelope.data ?? [];
}

/** Work's "removed" filter (ptc-docs plan 2026-11-02 §3): only the client's REMOVED journals, newest removal first. */
export function useRemovedJournals(clientId: string, enabled = true) {
  return useQuery({
    queryKey: [...journalKeys.byClient(clientId), 'removed'],
    queryFn: async () => {
      const envelope = await fetchApi<ApiEnvelope<ManualJournal[]>>(
        `/journals/${encodeURIComponent(clientId)}?removed=true`,
      );
      return envelope.data ?? [];
    },
    enabled: enabled && !!clientId,
  });
}

/** Brings a removed journal back (reversible: removing it again is the same soft delete). */
export function useRestoreJournal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, journalId }: { clientId: string; journalId: string }) => {
      await fetchApi<ApiEnvelope<never>>(
        `/journals/${encodeURIComponent(clientId)}/${encodeURIComponent(journalId)}/restore`,
        { method: 'POST' },
      );
    },
    onSuccess: (_d, v) => void qc.invalidateQueries({ queryKey: journalKeys.byClient(v.clientId) }),
  });
}

export function useJournals(clientId: string) {
  return useQuery({
    queryKey: journalKeys.byClient(clientId),
    queryFn: () => fetchJournals(clientId),
    enabled: !!clientId,
  });
}

/**
 * A saved journal, plus the sales split the save TAUGHT the client when it changed one (POS journals;
 * docs/client-chart-and-sales-split.md §3). Rides beside the journal rather than in it: it is about the client.
 */
export type SavedJournal = ManualJournal & { salesSplitLearned?: SalesSplit | null };
type SaveEnvelope = ApiEnvelope<ManualJournal> & { salesSplitLearned?: SalesSplit | null };

export async function createJournal(
  clientId: string,
  payload: Partial<ManualJournal>,
): Promise<SavedJournal> {
  const envelope = await fetchApi<SaveEnvelope>(
    `/journals/${encodeURIComponent(clientId)}`,
    { method: 'POST', body: JSON.stringify({ ...payload, clientId }) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? i18next.t('journalPage.errors.createFailed'));
  return { ...envelope.data, salesSplitLearned: envelope.salesSplitLearned ?? null };
}

export function useCreateJournal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, payload }: { clientId: string; payload: Partial<ManualJournal> }) =>
      createJournal(clientId, payload),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: journalKeys.byClient(variables.clientId) });
    },
  });
}

export async function updateJournal(
  clientId: string,
  journalId: string,
  payload: Partial<ManualJournal>,
): Promise<SavedJournal> {
  const envelope = await fetchApi<SaveEnvelope>(
    `/journals/${encodeURIComponent(clientId)}/${encodeURIComponent(journalId)}`,
    { method: 'PUT', body: JSON.stringify({ ...payload, clientId }) },
  );
  if (!envelope.data) throw new Error(envelope.error ?? i18next.t('journalPage.errors.saveFailed'));
  return { ...envelope.data, salesSplitLearned: envelope.salesSplitLearned ?? null };
}

export function useUpdateJournal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      journalId,
      payload,
    }: {
      clientId: string;
      journalId: string;
      payload: Partial<ManualJournal>;
    }) => updateJournal(clientId, journalId, payload),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: journalKeys.byClient(variables.clientId) });
    },
  });
}

export async function deleteJournal(clientId: string, journalId: string): Promise<void> {
  await fetchApi<unknown>(
    `/journals/${encodeURIComponent(clientId)}/${encodeURIComponent(journalId)}`,
    { method: 'DELETE' },
  );
}

export function useDeleteJournal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, journalId }: { clientId: string; journalId: string }) =>
      deleteJournal(clientId, journalId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: journalKeys.byClient(variables.clientId) });
    },
  });
}

/**
 * Ask a template for lines. Pure on the server — this writes nothing.
 *
 * A 412 carries the unmapped roles; they are surfaced on the thrown ExportError so
 * the caller can name them and link to the mapping page rather than showing prose.
 */
export async function proposeJournal(
  clientId: string,
  templateId: string,
  request: JournalProposalRequest,
): Promise<JournalProposal> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const token = await requireAccessToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    headers['Authorization'] = `Bearer ${token}`;

    const url =
      `${API_BASE}/journals/${encodeURIComponent(clientId)}` +
      `/templates/${encodeURIComponent(templateId)}/propose`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal: controller.signal,
    });

    const envelope = (await response.json().catch(() => null)) as
      | (ApiEnvelope<JournalProposal> & { missingRoles?: string[] })
      | null;

    if (!response.ok || !envelope?.data) {
      throw new ExportError(
        response.status,
        apiErrorMessage(response.status, envelope),
        envelope?.missingRoles,
      );
    }
    return envelope.data;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ExportError(0, timeoutMessage());
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function useProposeJournal() {
  return useMutation({
    mutationFn: ({
      clientId,
      templateId,
      request,
    }: {
      clientId: string;
      templateId: string;
      request: JournalProposalRequest;
    }) => proposeJournal(clientId, templateId, request),
  });
}

/**
 * Export a saved journal to Xero CSV. Validation lives here, not on save: a draft is
 * allowed to be half-built, and a 400 comes back with every error and its line index.
 */
export async function exportJournal(
  clientId: string,
  journalId: string,
  /** Accuracy Q3: the person ticked "I checked these figures against the return". */
  figuresChecked = false,
): Promise<ManualJournalResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const token = await requireAccessToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    headers['Authorization'] = `Bearer ${token}`;

    const url =
      `${API_BASE}/journals/${encodeURIComponent(clientId)}` +
      `/${encodeURIComponent(journalId)}/export`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ figuresChecked }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as {
        error?: string;
        code?: string;
        errors?: JournalValidationErrorBody[];
      };
      // Line-anchored errors are joined into one message so a single alert names
      // every field to fix, rather than one per round trip.
      const detail = body.errors?.length ? body.errors.map(journalValidationMessage).join(' ') : undefined;
      throw new ExportError(response.status, detail ?? apiErrorMessage(response.status, body));
    }

    const blob = await response.blob();
    const filename =
      parseFilename(response.headers.get('Content-Disposition')) ?? `journal-${journalId}.csv`;
    const summary = response.headers.get('X-Conversion-Summary') ?? '';
    return { blob, filename, summary };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ExportError(0, timeoutMessage());
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function useExportJournal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, journalId, figuresChecked }: { clientId: string; journalId: string; figuresChecked?: boolean }) =>
      exportJournal(clientId, journalId, figuresChecked),
    onSuccess: (_data, variables) => {
      // Export freezes the journal (409 on later edit), so the cached copy is stale.
      void qc.invalidateQueries({ queryKey: journalKeys.byClient(variables.clientId) });
    },
  });
}

// ── B3: correcting an exported journal supersedes it, never deletes it ──────

export async function correctJournal(clientId: string, journalId: string): Promise<ManualJournal> {
  const envelope = await fetchApi<ApiEnvelope<ManualJournal>>(
    `/journals/${encodeURIComponent(clientId)}/${encodeURIComponent(journalId)}/correct`,
    { method: 'POST' },
  );
  if (!envelope.data) throw new Error(apiErrorMessage(500, envelope));
  return envelope.data;
}

export function useCorrectJournal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, journalId }: { clientId: string; journalId: string }) => correctJournal(clientId, journalId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: journalKeys.byClient(variables.clientId) });
    },
  });
}

// ── B2: "landed in Xero", said by a person (docs/core-workflow.md) ───────────

export async function confirmXeroImport(
  clientId: string,
  journalId: string,
  xeroJournalNumber: string | null,
): Promise<ManualJournal> {
  const envelope = await fetchApi<ApiEnvelope<ManualJournal>>(
    `/journals/${encodeURIComponent(clientId)}/${encodeURIComponent(journalId)}/xero-import`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ xeroJournalNumber }) },
  );
  if (!envelope.data) throw new Error(apiErrorMessage(500, envelope));
  return envelope.data;
}

export function useConfirmXeroImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, journalId, xeroJournalNumber }: { clientId: string; journalId: string; xeroJournalNumber: string | null }) =>
      confirmXeroImport(clientId, journalId, xeroJournalNumber),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: journalKeys.byClient(variables.clientId) });
    },
  });
}

export async function withdrawXeroImport(clientId: string, journalId: string): Promise<ManualJournal> {
  const envelope = await fetchApi<ApiEnvelope<ManualJournal>>(
    `/journals/${encodeURIComponent(clientId)}/${encodeURIComponent(journalId)}/xero-import/withdraw`,
    { method: 'POST' },
  );
  if (!envelope.data) throw new Error(apiErrorMessage(500, envelope));
  return envelope.data;
}

export function useWithdrawXeroImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, journalId }: { clientId: string; journalId: string }) => withdrawXeroImport(clientId, journalId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: journalKeys.byClient(variables.clientId) });
    },
  });
}

// ── Conversion history (CONVERSIONS_SPEC.md §8) ──────────────────────────────
//
// C6 wired the recording side; this is the read side. Until now the history the
// backend collects had no way to reach a screen.

export const conversionKeys = {
  all: ['conversions'] as const,
  byClient: (clientId: string, filters?: ConversionHistoryFilters) =>
    [...conversionKeys.all, clientId, filters ?? {}] as const,
};

export async function fetchConversions(
  clientId: string,
  filters: ConversionHistoryFilters = {},
): Promise<ConversionRecord[]> {
  const params = new URLSearchParams();
  if (filters.flow) params.set('flow', filters.flow);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  const query = params.toString();

  const envelope = await fetchApi<ApiEnvelope<ConversionRecord[]>>(
    `/conversions/${encodeURIComponent(clientId)}${query ? `?${query}` : ''}`,
  );
  return envelope.data ?? [];
}

export function useConversions(clientId: string, filters: ConversionHistoryFilters = {}) {
  return useQuery({
    queryKey: conversionKeys.byClient(clientId, filters),
    queryFn: () => fetchConversions(clientId, filters),
    enabled: !!clientId,
  });
}

/**
 * Re-downloads a retained CSV.
 *
 * ⚠ A 404 here has TWO meanings the server deliberately distinguishes in its message:
 * "no such conversion" and "recorded, but the file was not retained". The error text is
 * surfaced verbatim rather than replaced with a generic one, because those send a
 * bookkeeper to different places. Callers should avoid reaching this at all for records
 * with no `blobPath` — see `ConversionRecord`.
 */
export async function downloadConversion(
  clientId: string,
  conversionId: string,
): Promise<FileExportResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const token = await requireAccessToken();
    const headers: Record<string, string> = {};
    headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(
      `${API_BASE}/conversions/${encodeURIComponent(clientId)}/${encodeURIComponent(conversionId)}/download`,
      { method: 'GET', headers, signal: controller.signal },
    );

    if (!response.ok) {
      throw new ExportError(response.status, apiErrorMessage(response.status, await readErrorBody(response)));
    }

    const blob = await response.blob();
    const filename = parseFilename(response.headers.get('Content-Disposition')) ?? 'conversion.csv';
    const summary = response.headers.get('X-Conversion-Summary') ?? '';
    return { blob, filename, summary };
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new ExportError(0, timeoutMessage());
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function useDownloadConversion() {
  return useMutation({
    mutationFn: ({ clientId, conversionId }: { clientId: string; conversionId: string }) =>
      downloadConversion(clientId, conversionId),
  });
}

// ── "Read the report" (wave 4 §5) ────────────────────────────────────────────
//
// Read-only on the server: it names the report's kind and the classes it prints, and (P2.5) its figures. A query, not a
// mutation, so the template's preview and the journal's button share one read per upload.

export const posReportKeys = {
  read: (clientId: string, documentId: string) => ['pos-report-read', clientId, documentId] as const,
};

export async function readPosReport(clientId: string, documentId: string): Promise<PosReportReadResult> {
  const envelope = await fetchApi<ApiEnvelope<PosReportReadResult>>(
    `/pos-reports/${encodeURIComponent(clientId)}/${encodeURIComponent(documentId)}/read`,
    { method: 'POST' },
  );
  return envelope.data!;
}

export function useReadPosReport(clientId: string, documentId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: posReportKeys.read(clientId, documentId ?? ''),
    queryFn: () => readPosReport(clientId, documentId!),
    enabled: enabled && !!clientId && !!documentId,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** The client's newest POS report upload that is not removed, or null: what the template's preview reads. */
export function latestPosReport(uploads: readonly UploadedDocument[] | undefined): UploadedDocument | null {
  return (uploads ?? [])
    .filter((u) => u.category === 'posReport' && !u.isDeleted)
    .sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt))[0] ?? null;
}
